import { useEffect, useState, forwardRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import classNames from 'classnames';

import type { ApiAccountJSON } from '@/mastodon/api_types/accounts';
import CircleGapIcon from '@/material-icons/400-24px/circle_gap.svg?react';
import GroupsIcon from '@/material-icons/400-24px/groups.svg?react';
import ListAltIcon from '@/material-icons/400-24px/list_alt.svg?react';
import { apiGetAudienceListMembers } from 'mastodon/api/audience_lists';
import { apiGetListAccounts } from 'mastodon/api/lists';
import { Avatar } from 'mastodon/components/avatar';
import { AvatarGroup } from 'mastodon/components/avatar_group';
import { Icon } from 'mastodon/components/icon';
import { LoadingIndicator } from 'mastodon/components/loading_indicator';
import { selectAccountStatus } from 'mastodon/selectors/statuses';
import { useAppSelector } from 'mastodon/store';

// Kept in sync with `visibility_button.tsx`'s labels where they overlap, but
// this hover card is meant to read as a plain-language confirmation of who
// can actually see the post -- not as the compose-time picker's copy.
const messages = defineMessages({
  public: {
    id: 'status.visibility_hover_card.public',
    defaultMessage: 'Public to all',
  },
  unlisted: {
    id: 'status.visibility_hover_card.unlisted',
    defaultMessage: 'Quiet public',
  },
  unlistedDescription: {
    id: 'status.visibility_hover_card.unlisted_description',
    defaultMessage: 'Visible to everyone, hidden from public timelines',
  },
  direct: {
    id: 'status.visibility_hover_card.direct',
    defaultMessage: 'Private mention',
  },
  directDescription: {
    id: 'status.visibility_hover_card.direct_description',
    defaultMessage: 'Only visible to the accounts mentioned',
  },
  private: {
    id: 'status.visibility_hover_card.private',
    defaultMessage: 'Private',
  },
  privateDescription: {
    id: 'status.visibility_hover_card.private_description',
    defaultMessage: 'Only visible to your followers',
  },
  group: {
    id: 'status.visibility_hover_card.group',
    defaultMessage: 'Private to group',
  },
  audienceList: {
    id: 'status.visibility_hover_card.audience_list',
    defaultMessage: 'Private to custom audience',
  },
  list: {
    id: 'status.visibility_hover_card.list',
    defaultMessage: 'Private to list',
  },
  membersUnavailable: {
    id: 'status.visibility_hover_card.members_unavailable',
    defaultMessage: 'Members unavailable',
  },
});

const PREVIEW_LIMIT = 5;

export const StatusVisibilityHoverCard = forwardRef<
  HTMLDivElement,
  { statusId?: string }
>(({ statusId }, ref) => {
  const intl = useIntl();
  const status = useAppSelector((state) =>
    statusId ? selectAccountStatus(state, statusId) : undefined,
  );

  const groupId = status?.group_id;
  const audienceListId = status?.audience_list_id;
  const listId = status?.list_id;

  const [members, setMembers] = useState<ApiAccountJSON[] | null>(null);

  useEffect(() => {
    setMembers(null);

    if (audienceListId) {
      apiGetAudienceListMembers(audienceListId, { limit: PREVIEW_LIMIT })
        .then((data) => {
          setMembers(data);
          return undefined;
        })
        .catch(() => {
          setMembers([]);
        });
    } else if (listId) {
      apiGetListAccounts(listId, PREVIEW_LIMIT)
        .then((data) => {
          setMembers(data);
          return undefined;
        })
        .catch(() => {
          setMembers([]);
        });
    }
  }, [audienceListId, listId]);

  return (
    <div
      ref={ref}
      id='visibility-hover-card'
      role='tooltip'
      className={classNames(
        'hover-card',
        'visibility-hover-card',
        'dropdown-animation',
      )}
    >
      {!status ? (
        <LoadingIndicator />
      ) : groupId ? (
        <>
          <strong className='visibility-hover-card__heading'>
            <Icon id='group' icon={GroupsIcon} />
            {intl.formatMessage(messages.group)}
          </strong>
          <span className='visibility-hover-card__detail'>
            {status.group_name}
          </span>
        </>
      ) : audienceListId || listId ? (
        <>
          <strong className='visibility-hover-card__heading'>
            <Icon
              id={audienceListId ? 'circle_gap' : 'list_alt'}
              icon={audienceListId ? CircleGapIcon : ListAltIcon}
              noFill={!!audienceListId}
            />
            {intl.formatMessage(
              audienceListId ? messages.audienceList : messages.list,
            )}
          </strong>
          {status.audience_list_name && (
            <span className='visibility-hover-card__detail'>
              {status.audience_list_name}
            </span>
          )}
          {status.list_title && (
            <span className='visibility-hover-card__detail'>
              {status.list_title}
            </span>
          )}
          {members === null ? (
            <LoadingIndicator />
          ) : members.length === 0 ? (
            <span className='visibility-hover-card__detail'>
              {intl.formatMessage(messages.membersUnavailable)}
            </span>
          ) : (
            <AvatarGroup>
              {members.map((account) => (
                <Avatar key={account.id} account={account} size={28} />
              ))}
            </AvatarGroup>
          )}
        </>
      ) : (
        <>
          <strong className='visibility-hover-card__heading'>
            {intl.formatMessage(
              status.visibility === 'public'
                ? messages.public
                : status.visibility === 'unlisted'
                  ? messages.unlisted
                  : status.visibility === 'direct'
                    ? messages.direct
                    : messages.private,
            )}
          </strong>
          {status.visibility !== 'public' && (
            <span className='visibility-hover-card__detail'>
              {intl.formatMessage(
                status.visibility === 'unlisted'
                  ? messages.unlistedDescription
                  : status.visibility === 'direct'
                    ? messages.directDescription
                    : messages.privateDescription,
              )}
            </span>
          )}
        </>
      )}
    </div>
  );
});

StatusVisibilityHoverCard.displayName = 'StatusVisibilityHoverCard';
